import { useEffect, useMemo, useRef, useState } from 'react'
import type { Resolver } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { IconBuilding, IconUser } from '@tabler/icons-react'
import { jwtDecode } from 'jwt-decode'

import { AccountType } from '@/api/v1/administration/account/account.enums'
import { useListCountries } from '@/api/v1/platform/countries'
import { CountryCombobox } from '@/components/common/country-combobox'
import { LoadingButton } from '@/components/common/loading-button'
import { PhoneInput } from '@/components/common/phone-input'
import {
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
  ResponsiveDialogForm,
} from '@/components/common/responsive-dialog'
import { Field, FieldError, FieldLabel } from '@/components/sui/field'
import { Input } from '@/components/sui/input'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useLocale } from '@/hooks/use-locale'
import { getCountryDisplayName } from '@/lib/intl/get-country-display-name'
import {
  getBrowserRegion,
  getEdgeCountry,
  LOCALE_TO_COUNTRY,
} from '@/lib/locale-country'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'

import type { AccountProfileData } from './account-profile-form.schema'
import { accountProfileSchema } from './account-profile-form.schema'
import { AccountProfileFormSkeleton } from './account-profile-form.skeleton'
import locales from './locales'

interface AccountProfileFormProps {
  onSubmit: (data: AccountProfileData) => void
  defaultValues?: Partial<AccountProfileData>
  isLoading?: boolean
  submitLabel?: string
}

export function AccountProfileForm({
  onSubmit,
  defaultValues,
  isLoading,
  submitLabel,
}: AccountProfileFormProps) {
  const { t } = useComponentTranslation(locales)
  const locale = useLocale()
  const { data: countriesData } = useListCountries()

  const countries = (countriesData?.items ?? [])
    .map((c) => ({
      ...c,
      displayName: c.code ? getCountryDisplayName(c.code, locale) : c.name,
    }))
    .sort((a, b) =>
      (a.displayName ?? '').localeCompare(b.displayName ?? '', locale),
    )

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm<AccountProfileData>({
    resolver: zodResolver(accountProfileSchema) as Resolver<AccountProfileData>,
    mode: 'onSubmit',
    defaultValues: {
      countryCode: defaultValues?.countryCode ?? '',
      type: defaultValues?.type ?? undefined,
      accountName: defaultValues?.accountName ?? '',
      phone: defaultValues?.phone ?? '',
    },
  })

  const countryValue = watch('countryCode')
  const typeValue = watch('type') as AccountType | undefined
  const phoneValue = watch('phone')
  const hasAutoSelected = useRef(!!defaultValues?.countryCode)
  // Gates the skeleton below. Starts true unless the caller already told us the
  // country, in which case there is nothing to resolve.
  const [isResolvingCountry, setIsResolvingCountry] = useState(
    !defaultValues?.countryCode,
  )

  useEffect(() => {
    if (hasAutoSelected.current || !countriesData?.items || countryValue) return
    hasAutoSelected.current = true

    // This field decides the tenant, and with it the billing entity and currency, so it
    // has to be seeded with where the person IS. Cloudflare already resolved that at the
    // edge; the browser region covers local dev, and the app language is the last resort
    // -- only that last step is a guess (pt-BR would put an Angolan on the Brazilian
    // tenant).
    //
    // No cleanup on purpose: the ref above already makes this fire once, and aborting the
    // in-flight lookup would mean a re-render (or StrictMode's double mount) cancels the
    // only attempt there will ever be, leaving the field blank.
    void getEdgeCountry()
      .then((edgeCountry) => {
        const code =
          edgeCountry || getBrowserRegion() || LOCALE_TO_COUNTRY[locale]
        if (code && countriesData.items.some((c) => c.code === code)) {
          setValue('countryCode', code)
        }
      })
      .finally(() => setIsResolvingCountry(false))
  }, [countriesData, countryValue, setValue, locale])

  // The access token carries the user's name claim — the only name source
  // when no account context exists yet (first-account onboarding).
  const accessToken = useAuthStore((state) => state.accessToken)
  const userName = useMemo(() => {
    if (!accessToken) return ''
    try {
      return jwtDecode<{ name?: string }>(accessToken).name ?? ''
    } catch {
      return ''
    }
  }, [accessToken])

  const handleTypeSelect = (nextType: AccountType) => {
    setValue('type', nextType, { shouldValidate: true })
    const currentName = getValues('accountName')
    if (nextType === AccountType.Personal) {
      // Personal accounts default to the user's own name, but never
      // overwrite something the user already typed.
      if (userName && !currentName.trim())
        setValue('accountName', userName, { shouldValidate: true })
    } else if (currentName === userName) {
      // Company accounts must not keep the auto-filled personal name;
      // custom input is always preserved.
      setValue('accountName', '')
    }
  }

  const typeOptions = [
    {
      value: AccountType.Company,
      icon: IconBuilding,
      title: t('fields.type.companyTitle'),
      description: t('fields.type.companyDescription'),
    },
    {
      value: AccountType.Personal,
      icon: IconUser,
      title: t('fields.type.personalTitle'),
      description: t('fields.type.personalDescription'),
    },
  ]

  // The country drives the phone's dialing code, so showing the fields before it is
  // known means both visibly change under the user. Wait for the country list and for
  // the location lookup, then render the form already settled.
  if (isResolvingCountry || !countriesData?.items)
    return <AccountProfileFormSkeleton />

  return (
    <ResponsiveDialogForm onSubmit={handleSubmit((data) => onSubmit(data))}>
      <ResponsiveDialogBody>
        <div className="space-y-4">
          <Field className="gap-1.5">
            <FieldLabel id="account-type-label">
              {t('fields.type.label')}
            </FieldLabel>
            <div
              role="radiogroup"
              aria-labelledby="account-type-label"
              aria-describedby={errors.type ? 'account-type-error' : undefined}
              className="grid grid-cols-2 gap-3"
            >
              {typeOptions.map((option) => {
                const isSelected = typeValue === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => handleTypeSelect(option.value)}
                    className={cn(
                      'flex cursor-pointer flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors sm:p-4',
                      'hover:border-primary/50 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                      isSelected
                        ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
                        : 'border-input',
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-9 items-center justify-center rounded-lg',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      <option.icon className="size-5" aria-hidden="true" />
                    </span>
                    <span className="text-sm font-semibold">
                      {option.title}
                    </span>
                    {/* Two cards side by side leave ~140px of text per card on a
                        phone: the description would wrap to four lines, so the
                        title carries the choice there. */}
                    <span className="text-muted-foreground hidden text-xs sm:block">
                      {option.description}
                    </span>
                  </button>
                )
              })}
            </div>
            <FieldError
              id="account-type-error"
              errors={
                errors.type
                  ? [{ message: t(errors.type.message as string) }]
                  : undefined
              }
            />
          </Field>

          <Field className="gap-1.5">
            <FieldLabel htmlFor="accountName">
              {t('fields.accountName.label')}
            </FieldLabel>
            <Input
              id="accountName"
              {...register('accountName')}
              placeholder={t('fields.accountName.placeholder')}
              aria-invalid={!!errors.accountName}
            />
            <FieldError
              errors={
                errors.accountName
                  ? [{ message: t(errors.accountName.message as string) }]
                  : undefined
              }
            />
          </Field>

          <Field className="gap-1.5">
            <FieldLabel htmlFor="countryCode">
              {t('fields.country.label')}
            </FieldLabel>
            <CountryCombobox
              id="countryCode"
              value={countryValue}
              onValueChange={(value) =>
                setValue('countryCode', value, { shouldValidate: true })
              }
              countries={countries}
              placeholder={t('fields.country.placeholder')}
              aria-invalid={!!errors.countryCode}
            />
            <FieldError
              errors={
                errors.countryCode
                  ? [{ message: t(errors.countryCode.message as string) }]
                  : undefined
              }
            />
          </Field>

          <Field className="gap-1.5">
            <FieldLabel htmlFor="phone">{t('fields.phone.label')}</FieldLabel>
            <PhoneInput
              id="phone"
              value={phoneValue}
              onChange={(value) =>
                setValue('phone', value, { shouldValidate: true })
              }
              placeholder={t('fields.phone.placeholder')}
              aria-invalid={!!errors.phone}
              country={countryValue}
            />
            <FieldError
              errors={
                errors.phone
                  ? [{ message: t(errors.phone.message as string) }]
                  : undefined
              }
            />
          </Field>
        </div>
      </ResponsiveDialogBody>
      <ResponsiveDialogFooter>
        <LoadingButton type="submit" size="lg" isLoading={isLoading}>
          {submitLabel ?? t('submit')}
        </LoadingButton>
      </ResponsiveDialogFooter>
    </ResponsiveDialogForm>
  )
}
